import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import Select from "react-select";
import type { ICustomerSelectItem } from "../../../customTypings/location/Location";
import type { ISiteOption } from "../../../customTypings/haulier/Haulier";
import { locationsService } from "../../../services/locations/locations.service";
import { sitesService } from "../../../services/sites/sites.service";
import { ReactSelectBootstrapStyle } from "../../../styles/ReactSelectBootstrapStyle";
import { getUserFacingErrorMessage, logErrorForDev, showToastError, showToastSuccess } from "../../../utils";

export interface AddCustomerAssociationModalProps {
    show: boolean;
    locationId: string;
    showSitePicker: boolean;
    defaultSiteId?: string | null;
    onClose: () => void;
    onAssociated: () => void;
}

const AddCustomerAssociationModal = ({
    show,
    locationId,
    showSitePicker,
    defaultSiteId,
    onClose,
    onAssociated,
}: AddCustomerAssociationModalProps) => {
    const [sites, setSites] = useState<ISiteOption[]>([]);
    const [siteId, setSiteId] = useState("");
    const [customers, setCustomers] = useState<ICustomerSelectItem[]>([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
    const [label, setLabel] = useState("");
    const [isMainLocation, setIsMainLocation] = useState(false);
    const [associating, setAssociating] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
        if (!show) {
            setSiteId("");
            setSelectedCustomerId(null);
            setLabel("");
            setIsMainLocation(false);
            setLoadError(null);
            return;
        }
        if (!showSitePicker && defaultSiteId) {
            setSiteId(defaultSiteId);
            return;
        }
        sitesService
            .listForOrganisation()
            .then((list) => {
                setSites(list);
                if (list.length === 1) {
                    setSiteId(list[0].id);
                } else if (defaultSiteId) {
                    setSiteId(defaultSiteId);
                }
            })
            .catch((e: unknown) => {
                logErrorForDev("AddCustomerAssociationModal.sites", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load sites."));
            });
    }, [show, showSitePicker, defaultSiteId]);

    useEffect(() => {
        if (!show || !siteId) {
            setCustomers([]);
            setSelectedCustomerId(null);
            return;
        }
        setCustomersLoading(true);
        locationsService
            .listAvailableCustomersForAssociation(locationId, siteId)
            .then((rows) => setCustomers(rows))
            .catch((e: unknown) => {
                logErrorForDev("AddCustomerAssociationModal.customers", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load customers."));
            })
            .finally(() => setCustomersLoading(false));
    }, [show, siteId, locationId]);

    const customerOptions = useMemo(
        () => customers.map((c) => ({ value: c.id, label: c.name })),
        [customers]
    );

    const selectedCustomerOption = useMemo(
        () => customerOptions.find((o) => o.value === selectedCustomerId) ?? null,
        [customerOptions, selectedCustomerId]
    );

    const handleAssociate = () => {
        if (!selectedCustomerId) return;
        setAssociating(true);
        locationsService
            .associateCustomer(locationId, {
                customerId: selectedCustomerId,
                label: label.trim() || null,
                isMainLocation,
            })
            .then(() => {
                showToastSuccess("Customer linked.");
                onAssociated();
            })
            .catch((e: unknown) => {
                showToastError(getUserFacingErrorMessage(e, "Could not link customer."));
            })
            .finally(() => setAssociating(false));
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Link a customer</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loadError && <div className="text-danger mb-3">{loadError}</div>}
                <div className="row g-3 mb-3">
                    {showSitePicker && (
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Label>Site</Form.Label>
                                <Form.Select
                                    value={siteId}
                                    onChange={(e) => {
                                        setSiteId(e.target.value);
                                        setSelectedCustomerId(null);
                                    }}
                                    aria-label="Site"
                                >
                                    <option value="">Select a site…</option>
                                    {sites.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.siteName}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                    )}
                    <div className={showSitePicker ? "col-md-8" : "col-12"}>
                        <Form.Group>
                            <Form.Label>Customer</Form.Label>
                            <Select
                                inputId="add-customer-association-customer"
                                options={customerOptions}
                                value={selectedCustomerOption}
                                onChange={(option) => setSelectedCustomerId(option?.value ?? null)}
                                isDisabled={!siteId || customersLoading}
                                isLoading={customersLoading}
                                isClearable
                                placeholder={
                                    siteId
                                        ? customersLoading
                                          ? "Loading…"
                                          : "Select a customer…"
                                        : "Choose a site first"
                                }
                                styles={ReactSelectBootstrapStyle}
                                classNamePrefix="react-select"
                            />
                        </Form.Group>
                    </div>
                </div>
                <div className="mb-3">
                    <Form.Group>
                        <Form.Label>Label</Form.Label>
                        <Form.Control
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="Optional"
                        />
                    </Form.Group>
                </div>
                <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
                    <Form.Check
                        type="checkbox"
                        id="add-customer-association-main"
                        label="Set as primary address"
                        checked={isMainLocation}
                        onChange={(e) => setIsMainLocation(e.target.checked)}
                        disabled={!selectedCustomerId || associating}
                    />
                    <Button
                        variant="primary"
                        onClick={handleAssociate}
                        disabled={!selectedCustomerId || associating}
                    >
                        {associating ? "Linking…" : "Link customer"}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export { AddCustomerAssociationModal };
